import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useLanguageStore } from '../../store/languageStore';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: string;
  required?: boolean;
  icon?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  success,
  required = false,
  icon,
  className = '',
  ...props
}) => {
  const { isRTL } = useLanguageStore();

  // Enhanced classes with design system
  const formControlClass = `form-control input-modern ${error ? 'is-invalid' : success ? 'is-valid' : ''} ${className}`;

  return (
    <motion.div
      className="mb-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {label && (
        <label 
          className={`form-label fw-semibold mb-2 d-block ${isRTL ? 'text-end' : 'text-start'}`} 
          style={{ 
            fontSize: '0.875rem', 
            color: '#374151',
            fontFamily: 'inherit',
            letterSpacing: '0.3px',
          }}
        >
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      
      {icon ? (
        <motion.div
          className="input-group"
          whileFocus={{ scale: 1.01 }}
          style={{
            borderRadius: '0.75rem',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          {!isRTL && (
            <span 
              className="input-group-text bg-white border-0 d-flex align-items-center justify-content-center" 
              style={{ 
                width: '48px',
                minWidth: '48px',
                padding: '0.75rem',
                borderTopLeftRadius: '0.75rem',
                borderBottomLeftRadius: '0.75rem',
                borderRight: 'none',
              }}
            >
              <span style={{ color: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
              </span>
            </span>
          )}
          <input
            className={formControlClass}
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: isRTL ? '0.75rem 0 0 0.75rem' : '0 0.75rem 0.75rem 0',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              color: '#1f2937',
              fontFamily: 'inherit',
              boxShadow: 'none',
              flex: 1,
              ...(error && { 
                boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)',
                borderColor: '#ef4444',
              }),
              ...(success && { 
                boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
                borderColor: '#10b981',
              }),
            }}
            {...props}
          />
          {isRTL && (
            <span 
              className="input-group-text bg-white border-0 d-flex align-items-center justify-content-center" 
              style={{ 
                width: '48px',
                minWidth: '48px',
                padding: '0.75rem',
                borderTopRightRadius: '0.75rem',
                borderBottomRightRadius: '0.75rem',
                borderLeft: 'none',
              }}
            >
              <span style={{ color: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {icon}
              </span>
            </span>
          )}
        </motion.div>
      ) : (
        <motion.div whileFocus={{ scale: 1.01 }}>
          <input
            className={formControlClass}
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{
              backgroundColor: '#ffffff',
              border: 'none',
              borderRadius: '0.75rem',
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              color: '#1f2937',
              fontFamily: 'inherit',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              ...(error && { borderColor: '#ef4444', boxShadow: '0 0 0 2px rgba(239, 68, 68, 0.2)' }),
              ...(success && { borderColor: '#10b981', boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)' }),
            }}
            {...props}
          />
        </motion.div>
      )}
      
      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -5, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`mt-2 ${isRTL ? 'text-end' : 'text-start'}`}
          >
            <div className="d-flex align-items-start gap-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="flex-shrink-0"
                style={{ marginTop: '2px' }}
              >
                <AlertCircle className="h-4 w-4" style={{ color: 'var(--color-error)' }} />
              </motion.div>
              <motion.p
                initial={{ opacity: 0, x: isRTL ? 5 : -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-0 small fw-medium"
                style={{ color: 'var(--color-error)', fontSize: '0.875rem' }}
              >
                {error}
              </motion.p>
            </div>
          </motion.div>
        )}
        {success && !error && (
          <motion.div
            initial={{ opacity: 0, y: -5, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -5, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`mt-2 ${isRTL ? 'text-end' : 'text-start'}`}
          >
            <div className="d-flex align-items-start gap-2">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="flex-shrink-0"
                style={{ marginTop: '2px' }}
              >
                <CheckCircle2 className="h-4 w-4" style={{ color: 'var(--color-success)' }} />
              </motion.div>
              <motion.p
                initial={{ opacity: 0, x: isRTL ? 5 : -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="mb-0 small fw-medium"
                style={{ color: 'var(--color-success)', fontSize: '0.875rem' }}
              >
                {success}
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
